import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, MapPin, Banknote, HandCoins, MessageSquare,
  CheckCircle2, AlertTriangle, Edit3, Trash2, User as UserIcon,
} from 'lucide-react';
import { AgentLayout } from '../../../components/AgentLayout';
import { captureGPS } from '../../../gps';
import api from '../../../api';
import { encodeLan } from '../../../api';

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`}>{msg}</div>;
}

function fmtINR(n) {
  const num = Number(n || 0);
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  } catch { return d; }
}

function fmtDay(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }); } catch { return d; }
}

const STATUSES = [
  ['new_overdue', 'New Overdue'],
  ['contact_attempted', 'Contact Attempted'],
  ['connected', 'Connected'],
  ['ptp_set', 'PTP Set'],
  ['partially_paid', 'Partially Paid'],
  ['regularized', 'Regularized'],
  ['settled_closed', 'Settled / Closed'],
  ['dispute_hold', 'Dispute Hold'],
  ['escalated_supervisor', 'Escalated'],
];
const STATUS_LABELS = Object.fromEntries(STATUSES);

const ACTION_TYPES = [
  ['call', 'Call'],
  ['visit', 'Field Visit'],
  ['sms', 'SMS'],
  ['whatsapp', 'WhatsApp'],
  ['meeting', 'Meeting'],
];

function getGps() {
  // Synchronous best-effort fallback (used as a backup if captureGPS hasn't resolved yet)
  try {
    const raw = localStorage.getItem('au_gps');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed?.lat && parsed?.lng) return { gps_lat: parsed.lat, gps_lng: parsed.lng };
  } catch {}
  return {};
}

async function freshGps() {
  const c = await captureGPS();
  if (c && c.lat && c.lng) return { gps_lat: c.lat, gps_lng: c.lng };
  return getGps();
}

export default function AgentRecoveryCaseDetail() {
  const { loanAccountNumber } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, msg: '' });

  const [sheet, setSheet] = useState(null); // 'action' | 'promise' | 'payment'
  const [editingId, setEditingId] = useState(null); // id of event being edited (null = create)
  const [editReason, setEditReason] = useState('');

  const [actionForm, setActionForm] = useState({ actionType: 'call', outcome: '', notes: '', nextFollowupDate: '', statusChange: '' });
  const [promiseForm, setPromiseForm] = useState({ promiseAmount: '', promiseDate: '', notes: '' });
  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMode: 'cash', transactionRef: '', notes: '' });

  // Submit-button guard: prevents double-tap / React-fast-resubmit from
  // creating duplicate rows. Ref handles same-tick re-entry; state mirrors
  // it so the Sheet's button can render as disabled.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/recovery/cases/${encodeLan(loanAccountNumber)}`);
      setData(res.data);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  }, [loanAccountNumber]);

  useEffect(() => { load(); }, [load]);

  const submitAction = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (editingId) {
        await api.patch(`/recovery/actions/${editingId}`, {
          actionType: actionForm.actionType,
          outcome: actionForm.outcome,
          notes: actionForm.notes,
          nextFollowupDate: actionForm.nextFollowupDate || null,
          reason: editReason || null,
        });
      } else {
        const gps = await freshGps();
        const { statusChange, ...actionPayload } = actionForm;
        const wantsFlip = statusChange && statusChange !== c?.status;
        await api.post(`/recovery/cases/${encodeLan(loanAccountNumber)}/actions`, {
          ...actionPayload,
          nextFollowupDate: actionForm.nextFollowupDate || null,
          ...(wantsFlip ? { newStatus: statusChange } : {}),
          ...gps,
        });
      }
      closeSheet();
      showToast(editingId ? 'Action updated' : 'Action logged');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const submitPromise = async () => {
    if (!promiseForm.promiseDate || Number(promiseForm.promiseAmount) <= 0) {
      showToast('Amount and date are required');
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/recovery/promises/${editingId}`, {
          promiseAmount: parseFloat(promiseForm.promiseAmount),
          promiseDate: promiseForm.promiseDate,
          notes: promiseForm.notes,
          reason: editReason || null,
        });
      } else {
        const gps = await freshGps();
        await api.post(`/recovery/cases/${encodeLan(loanAccountNumber)}/promises`, {
          promiseAmount: parseFloat(promiseForm.promiseAmount),
          promiseDate: promiseForm.promiseDate,
          notes: promiseForm.notes,
          ...gps,
        });
      }
      closeSheet();
      showToast(editingId ? 'PTP updated' : 'PTP recorded');
      load();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
  };

  const submitPayment = async () => {
    if (Number(paymentForm.amount) <= 0) { showToast('Amount must be > 0'); return; }
    try {
      if (editingId) {
        await api.patch(`/recovery/payments/${editingId}`, {
          amount: parseFloat(paymentForm.amount),
          paymentMode: paymentForm.paymentMode,
          transactionRef: paymentForm.transactionRef,
          notes: paymentForm.notes,
          reason: editReason || null,
        });
      } else {
        const gps = await freshGps();
        await api.post(`/recovery/cases/${encodeLan(loanAccountNumber)}/payments`, {
          amount: parseFloat(paymentForm.amount),
          paymentMode: paymentForm.paymentMode,
          transactionRef: paymentForm.transactionRef,
          notes: paymentForm.notes,
          ...gps,
        });
      }
      closeSheet();
      showToast(editingId ? 'Payment updated' : 'Payment recorded');
      load();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
  };

  const closeSheet = () => {
    setSheet(null);
    setEditingId(null);
    setEditReason('');
    setActionForm({ actionType: 'call', outcome: '', notes: '', nextFollowupDate: '', statusChange: '' });
    setPromiseForm({ promiseAmount: '', promiseDate: '', notes: '' });
    setPaymentForm({ amount: '', paymentMode: 'cash', transactionRef: '', notes: '' });
  };

  const startEdit = (ev) => {
    const d = ev.data;
    setEditingId(d.id);
    setEditReason('');
    if (ev.kind === 'action') {
      setActionForm({
        actionType: d.actionType || 'call',
        outcome: d.outcome || '',
        notes: d.notes || '',
        nextFollowupDate: d.nextFollowupDate || '',
        statusChange: '',
      });
      setSheet('action');
    } else if (ev.kind === 'promise') {
      setPromiseForm({
        promiseAmount: String(d.promiseAmount || ''),
        promiseDate: d.promiseDate || '',
        notes: d.notes || '',
      });
      setSheet('promise');
    } else if (ev.kind === 'payment') {
      setPaymentForm({
        amount: String(d.amount || ''),
        paymentMode: d.paymentMode || 'cash',
        transactionRef: d.transactionRef || '',
        notes: d.notes || '',
      });
      setSheet('payment');
    }
  };

  const deleteEvent = async (ev) => {
    const labels = { action: 'this action', promise: 'this PTP', payment: 'this payment' };
    const reason = window.prompt(`Delete ${labels[ev.kind]}? Optional reason for audit log:`, '');
    if (reason === null) return; // cancelled
    try {
      const path = ev.kind === 'action' ? 'actions' : ev.kind === 'promise' ? 'promises' : 'payments';
      await api.delete(`/recovery/${path}/${ev.data.id}`, { params: reason ? { reason } : {} });
      showToast('Deleted');
      load();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
  };

  if (loading || !data) {
    return (
      <AgentLayout>
        <div className="au-page">
          <div className="empty-state fu">Loading…</div>
        </div>
      </AgentLayout>
    );
  }

  const { case: c, loan, customer, actions, promises, payments } = data;
  const timeline = [
    ...(actions || []).map((a) => ({ kind: 'action', ts: a.createdAt, data: a })),
    ...(promises || []).map((p) => ({ kind: 'promise', ts: p.createdAt, data: p })),
    ...(payments || []).map((p) => ({ kind: 'payment', ts: p.createdAt, data: p })),
  ].sort((a, b) => new Date(b.ts) - new Date(a.ts));

  return (
    <AgentLayout>
      <div className="au-page">
        {/* Back */}
        <div className="fu" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <button
            onClick={() => navigate('/recovery/cases')}
            style={{ background: 'none', border: 'none', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: 600, padding: 0, cursor: 'pointer' }}
          >
            <ArrowLeft size={14} /> Back to cases
          </button>
        </div>

        {/* Borrower card */}
        <div className="mcard fu-1">
          <div className="mc-body">
            <div className="mc-top">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mc-nm" style={{ fontSize: '16px' }}>{customer?.fullName || c.clientId}</div>
                <div className="mc-ow">Loan {c.loanAccountNumber}</div>
              </div>
              <span className="status-pill badge-today" style={{ fontSize: '10px' }}>
                {STATUS_LABELS[c.status] || c.status}
              </span>
            </div>
            <div className="mc-meta">
              {customer?.primaryPhone && (
                <a href={`tel:${customer.primaryPhone}`} className="mc-mi clickable">
                  <Phone size={12} /> {customer.primaryPhone}
                </a>
              )}
              {customer?.address && (
                <span className="mc-mi"><MapPin size={12} /> {customer.address}</span>
              )}
            </div>
            <div className="mc-foot">
              <div>
                <div style={{ fontSize: '10px', color: 'var(--g3)' }}>OUTSTANDING</div>
                <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: '15px' }}>{fmtINR(loan?.totalOverdueAmount)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '10px', color: 'var(--g3)' }}>DPD</div>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '13px' }}>{loan?.dpdDays ?? 0}d ({loan?.dpdBucket || '0'})</div>
                <div style={{ fontSize: '10px', color: (loan?.bouncedEmis ?? 0) > 0 ? 'var(--red)' : 'var(--g3)', fontWeight: 600 }}>
                  {(loan?.bouncedEmis ?? 0) > 0 ? `${loan.bouncedEmis} EMI bounced` : 'No EMI bounced'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loan details mini */}
        <div className="mcard fu-2">
          <div className="mc-body" style={{ padding: '10px 14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', fontSize: '12px' }}>
              <div><div style={{ color: 'var(--g3)', fontSize: '10px' }}>EMI</div><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmtINR(loan?.emiAmount)}</div></div>
              <div><div style={{ color: 'var(--g3)', fontSize: '10px' }}>NEXT DUE</div><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmtDay(loan?.nextDueDate)}</div></div>
              <div><div style={{ color: 'var(--g3)', fontSize: '10px' }}>FOLLOW-UP</div><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmtDay(c.nextFollowupDate)}</div></div>
              <div><div style={{ color: 'var(--g3)', fontSize: '10px' }}>LAST PAID</div><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmtDay(loan?.lastPaidDate)}</div></div>
            </div>
          </div>
        </div>

        {/* Action buttons grid */}
        <div className="fu-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '10px', marginBottom: '14px' }}>
          <ActionBtn icon={<Banknote size={16} />} label="Record Payment" primary onClick={() => setSheet('payment')} style={{ gridColumn: '1 / -1' }} />
          <ActionBtn icon={<HandCoins size={16} />} label="Add PTP" onClick={() => setSheet('promise')} />
          <ActionBtn icon={<MessageSquare size={16} />} label="Log Action" onClick={() => { setActionForm((p) => ({ ...p, statusChange: '' })); setSheet('action'); }} />
        </div>

        {/* Timeline */}
        <div className="sec-hd fu-4">
          <span className="sec-t">Activity Timeline</span>
          <span className="sec-l">{timeline.length} event{timeline.length !== 1 ? 's' : ''}</span>
        </div>
        {timeline.length === 0 ? (
          <div className="empty-state fu-5">
            <div className="empty-title">No activity yet</div>
            <div className="empty-text">Log your first action to start tracking.</div>
          </div>
        ) : timeline.map((ev, i) => <TimelineRow key={ev.data.id || i} ev={ev} onEdit={() => startEdit(ev)} onDelete={() => deleteEvent(ev)} />)}

        <div style={{ height: '20px' }} />
      </div>

      {/* Bottom Sheets */}
      {sheet === 'action' && (
        <Sheet title={editingId ? 'Edit Action' : 'Log Action'} icon={<MessageSquare size={16} />} onClose={closeSheet} onSubmit={submitAction} submitLabel={editingId ? 'Save' : 'Save'} submitting={submitting}>
          <div className="fg">
            <label className="fl">Type</label>
            <select className="fi" value={actionForm.actionType} onChange={(e) => setActionForm((p) => ({ ...p, actionType: e.target.value }))}>
              {ACTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="fg">
            <label className="fl">Outcome</label>
            <input className="fi" placeholder="e.g. customer not home" value={actionForm.outcome} onChange={(e) => setActionForm((p) => ({ ...p, outcome: e.target.value }))} />
          </div>
          <div className="fg">
            <label className="fl">Notes</label>
            <textarea className="fi" rows={2} value={actionForm.notes} onChange={(e) => setActionForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>
          {!editingId && (
            <div className="fg">
              <label className="fl">Update Status (optional)</label>
              <select className="fi" value={actionForm.statusChange} onChange={(e) => setActionForm((p) => ({ ...p, statusChange: e.target.value }))}>
                <option value="">— No change (currently {STATUS_LABELS[c?.status] || c?.status || '—'}) —</option>
                {STATUSES.filter(([v]) => v !== c?.status).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}
          <div className="fg">
            <label className="fl">Next Follow-up</label>
            <input className="fi" type="date" value={actionForm.nextFollowupDate} onChange={(e) => setActionForm((p) => ({ ...p, nextFollowupDate: e.target.value }))} />
          </div>
          {editingId && (
            <div className="fg">
              <label className="fl">Reason for edit (audit)</label>
              <input className="fi" placeholder="e.g. corrected outcome" value={editReason} onChange={(e) => setEditReason(e.target.value)} />
            </div>
          )}
        </Sheet>
      )}

      {sheet === 'promise' && (
        <Sheet title={editingId ? 'Edit PTP' : 'Promise to Pay'} icon={<HandCoins size={16} />} onClose={closeSheet} onSubmit={submitPromise} submitLabel={editingId ? 'Save' : 'Record PTP'}>
          <div className="fg">
            <label className="fl">Amount</label>
            <input className="fi" type="number" inputMode="numeric" placeholder="0" value={promiseForm.promiseAmount} onChange={(e) => setPromiseForm((p) => ({ ...p, promiseAmount: e.target.value }))} />
          </div>
          <div className="fg">
            <label className="fl">Promise Date</label>
            <input className="fi" type="date" value={promiseForm.promiseDate} onChange={(e) => setPromiseForm((p) => ({ ...p, promiseDate: e.target.value }))} />
          </div>
          <div className="fg">
            <label className="fl">Notes</label>
            <textarea className="fi" rows={2} value={promiseForm.notes} onChange={(e) => setPromiseForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>
          {editingId && (
            <div className="fg">
              <label className="fl">Reason for edit (audit)</label>
              <input className="fi" placeholder="e.g. customer revised date" value={editReason} onChange={(e) => setEditReason(e.target.value)} />
            </div>
          )}
        </Sheet>
      )}

      {sheet === 'payment' && (
        <Sheet title={editingId ? 'Edit Payment' : 'Record Payment'} icon={<Banknote size={16} />} onClose={closeSheet} onSubmit={submitPayment} submitLabel={editingId ? 'Save' : 'Record Payment'}>
          <div className="fg">
            <label className="fl">Amount</label>
            <input className="fi" type="number" inputMode="numeric" placeholder="0" value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} autoFocus />
          </div>
          <div className="fg">
            <label className="fl">Mode</label>
            <select className="fi" value={paymentForm.paymentMode} onChange={(e) => setPaymentForm((p) => ({ ...p, paymentMode: e.target.value }))}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          <div className="fg">
            <label className="fl">Transaction Ref</label>
            <input className="fi" placeholder="optional" value={paymentForm.transactionRef} onChange={(e) => setPaymentForm((p) => ({ ...p, transactionRef: e.target.value }))} />
          </div>
          <div className="fg">
            <label className="fl">Notes</label>
            <textarea className="fi" rows={2} value={paymentForm.notes} onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>
          {editingId && (
            <div className="fg">
              <label className="fl">Reason for edit (audit)</label>
              <input className="fi" placeholder="e.g. corrected amount" value={editReason} onChange={(e) => setEditReason(e.target.value)} />
            </div>
          )}
        </Sheet>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </AgentLayout>
  );
}

function ActionBtn({ icon, label, onClick, primary, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        padding: '14px 8px',
        background: primary ? 'var(--navy)' : '#fff',
        color: primary ? '#fff' : 'var(--navy)',
        border: primary ? '1px solid var(--navy)' : '1px solid var(--g2)',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 700,
        fontFamily: 'var(--fh)',
        cursor: 'pointer',
        ...(style || {}),
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function Sheet({ title, icon, onClose, onSubmit, submitLabel, submitting, children }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(3, 35, 96, 0.45)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: '420px',
          background: '#fff',
          borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
          padding: '16px 16px 24px',
          marginBottom: '110px',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.15)',
          maxHeight: 'calc(85vh - 110px)', overflowY: 'auto',
        }}
      >
        <div style={{ width: '36px', height: '4px', background: 'var(--g2)', borderRadius: '2px', margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--g1)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </div>
          <div style={{ fontWeight: 700, fontFamily: 'var(--fh)', color: 'var(--navy)', fontSize: '15px' }}>{title}</div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: '22px', color: 'var(--g3)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button onClick={onClose} className="sub-btn" style={{ flex: 1, background: '#fff', color: 'var(--navy)', border: '1px solid var(--g2)' }} disabled={submitting}>Cancel</button>
          <button onClick={onSubmit} className="sub-btn" style={{ flex: 2, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }} disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function TimelineRow({ ev, onEdit, onDelete }) {
  const { kind, ts, data: d } = ev;
  let icon, color, title, body;
  if (kind === 'action') {
    icon = <MessageSquare size={14} />;
    color = 'var(--navy)';
    title = `${d.actionType?.replace(/_/g, ' ')}${d.outcome ? ` · ${d.outcome}` : ''}`;
    body = d.notes;
  } else if (kind === 'promise') {
    const effStatus = (d.effectiveStatus || d.status || 'pending').toLowerCase();
    const isMissed = effStatus === 'broken';
    const isKept = effStatus === 'kept';
    const isPartial = effStatus === 'partially_kept';
    if (isKept) { icon = <CheckCircle2 size={14} />; color = '#16a34a'; }
    else if (isPartial) { icon = <HandCoins size={14} />; color = '#b45309'; }
    else if (isMissed) { icon = <AlertTriangle size={14} />; color = 'var(--red)'; }
    else { icon = <HandCoins size={14} />; color = '#0891b2'; }
    title = `PTP ${fmtINR(d.promiseAmount)} by ${fmtDay(d.promiseDate)}`;
    let label;
    if (isKept) label = `KEPT · paid ${fmtINR(d.paidAmount || d.promiseAmount)} ✓`;
    else if (isPartial) label = `PARTIALLY KEPT · paid ${fmtINR(d.paidAmount || 0)} of ${fmtINR(d.promiseAmount)}`;
    else if (isMissed) label = 'MISSED';
    else label = 'Pending';
    body = `${label}${d.notes ? ` · ${d.notes}` : ''}`;
  } else {
    icon = <CheckCircle2 size={14} />;
    color = '#16a34a';
    title = `Payment ${fmtINR(d.amount)} · ${d.paymentMode}`;
    body = `${d.transactionRef ? `Ref ${d.transactionRef}` : ''}${d.notes ? ` · ${d.notes}` : ''}` || null;
  }
  const hasGps = d.gps_lat && d.gps_lng;
  const wasEdited = !!d.updatedAt;
  const byName = d.createdByUserName || d.createdByEmployeeId;
  const byRole = d.createdByUserRole;
  return (
    <div style={{ display: 'flex', gap: '10px', padding: '10px 12px', background: '#fff', borderRadius: '10px', border: '1px solid var(--g2)', marginBottom: '8px' }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--g1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: 600, textTransform: 'capitalize' }}>{title}</div>
        {body && <div style={{ fontSize: '12px', color: 'var(--g3)', marginTop: '2px' }}>{body}</div>}
        <div style={{ fontSize: '11px', color: 'var(--g4)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>{fmtDate(ts)}</span>
          {byName && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'var(--navy)', fontWeight: 600 }}>
              <UserIcon size={11} /> by {byName}{byRole ? ` (${byRole})` : ''}
            </span>
          )}
          {wasEdited && <span style={{ color: 'var(--g3)', fontStyle: 'italic' }}>edited</span>}
          {hasGps && (
            <a
              href={`https://www.google.com/maps?q=${d.gps_lat},${d.gps_lng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--navy)', display: 'inline-flex', alignItems: 'center', gap: '2px', fontWeight: 600 }}
            >
              <MapPin size={11} /> Map
            </a>
          )}
        </div>
      </div>
      {(onEdit || onDelete) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
          {onEdit && (
            <button onClick={onEdit} aria-label="Edit" style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'var(--g3)' }}>
              <Edit3 size={14} />
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} aria-label="Delete" style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'var(--red)' }}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
